import { AUTO_PAR_NIVEAU } from "./timeCost.js";
export const GESTION_TASKS = [
    { name: "Devis et facturation", niveau: "high", renfort: "assistance administrative" },
    { name: "Relances de clients et d'impayés", niveau: "high", renfort: "assistance administrative" },
    { name: "Réponses aux demandes récurrentes", niveau: "high", renfort: "assistance administrative" },
    { name: "Prospection et présence en ligne", niveau: "mid", renfort: "appui commercial" },
    { name: "Comptabilité, déclarations et pièces justificatives", niveau: "mid", renfort: "prestataire comptable" },
    { name: "Administratif et organisation", niveau: "mid", renfort: "assistance administrative" },
];
/** Plafond d'heures déclarables par tâche et par semaine. */
export const GESTION_HEURES_MAX = 40;
/**
 * Les heures de gestion ramenées dans les clous : une valeur par tâche de
 * `GESTION_TASKS`, finie, entre 0 et `GESTION_HEURES_MAX`. `null` quand rien
 * d'exploitable n'a été transmis — ce qui est le cas de tout salarié.
 */
export function heuresGestionValides(brut) {
    if (!Array.isArray(brut))
        return null;
    const heures = GESTION_TASKS.map((_, i) => {
        const v = Number(brut[i]);
        return Number.isFinite(v) && v > 0 ? Math.min(Math.round(v * 2) / 2, GESTION_HEURES_MAX) : 0;
    });
    return heures.some((h) => h > 0) ? heures : null;
}
/**
 * Les tâches de gestion déclarées, au format d'une tâche du métier.
 *
 * Une tâche à zéro heure n'est pas retournée : elle ne pèse rien, et le rapport
 * n'a pas à en parler.
 */
export function tachesDeGestion(heures) {
    const valides = heuresGestionValides(heures);
    if (valides === null)
        return [];
    return GESTION_TASKS.map((tache, i) => ({ texte: tache.name, niveau: tache.niveau, heures: valides[i] })).filter((t) => t.heures > 0);
}
/**
 * Le temps de gestion récupérable, regroupé par renfort évité.
 *
 * Même formule que `recuperablesParSemaine` — heures × `AUTO_PAR_NIVEAU` —,
 * donc la somme des lignes est exactement la part de gestion du plafond de la
 * couverture. Les renforts sans heure déclarée sont omis ; l'ordre est celui
 * de leur première apparition dans `GESTION_TASKS`.
 */
export function renfortsEvites(heures) {
    const valides = heuresGestionValides(heures);
    if (valides === null)
        return [];
    const parRenfort = new Map();
    GESTION_TASKS.forEach((tache, i) => {
        const h = valides[i];
        if (h <= 0)
            return;
        const cumul = parRenfort.get(tache.renfort) ?? { heuresDeclarees: 0, recuperables: 0 };
        cumul.heuresDeclarees += h;
        cumul.recuperables += h * AUTO_PAR_NIVEAU[tache.niveau];
        parRenfort.set(tache.renfort, cumul);
    });
    return [...parRenfort.entries()].map(([renfort, c]) => ({
        renfort,
        heuresDeclarees: Math.round(c.heuresDeclarees * 10) / 10,
        recuperables: Math.round(c.recuperables * 10) / 10,
    }));
}
/**
 * Ce qui est déjà compté dans les tâches du métier — 24/09/2026.
 *
 * ## Le défaut, trouvé en production
 *
 * Les tâches du métier viennent du modèle, et pour un graphiste il y range
 * « Préparer le devis et la facture client ». Le bloc de gestion redemandait
 * juste en dessous « Devis et facturation ». La même semaine était donc
 * déclarée deux fois, et le coût du temps, la couverture du rapport et la
 * partie « Ce que vous n'aurez pas à embaucher » comptaient deux fois les
 * mêmes heures. Aucun test ne pouvait le voir : il fallait les tâches réelles
 * d'un vrai métier.
 *
 * ## La règle
 *
 * Une tâche de gestion dont le motif apparaît dans une tâche du métier est
 * **déjà comptée** : l'écran la montre sans curseur, en nommant la tâche qui la
 * couvre, et le serveur ramène ses heures à zéro. Dans le doute, on retire la
 * ligne de gestion plutôt que celle du métier : le métier porte l'indice, et
 * compter une heure en moins est un défaut ; la compter deux fois est une
 * promesse fausse, imprimée sur la couverture.
 */
const MOTIFS_GESTION = [
    /\bdevis\b|factur/i,
    /relanc|impay|recouvr|créances?\b/i,
    /demandes? (récurrentes|des clients|client)|questions? (récurrentes|des clients|client)|service client/i,
    /prospect|présence en ligne/i,
    /comptab|déclarations? (fiscales?|sociales?)|pièces? justificatives?/i,
    /administrati/i,
];
/**
 * Pour chaque tâche de gestion, la tâche du métier qui la couvre déjà, ou
 * `null`. Même ordre que `GESTION_TASKS`.
 */
export function gestionDejaComptee(tachesMetier) {
    return MOTIFS_GESTION.map((motif) => tachesMetier.find((t) => motif.test(t)) ?? null);
}
/**
 * Les heures de gestion, sans ce que le métier compte déjà. `null` quand il ne
 * reste rien — ce qui est aussi le cas de tout salarié.
 */
export function heuresGestionSansDoublon(brut, tachesMetier) {
    const valides = heuresGestionValides(brut);
    if (valides === null)
        return null;
    const couvertes = gestionDejaComptee(tachesMetier);
    return heuresGestionValides(valides.map((h, i) => (couvertes[i] === null ? h : 0)));
}
